import { useState } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import { CUSTOMERS_CSV_MAX_ROWS, customersCsvFilename } from '@/api/customers'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { usePermissions } from '@/lib/access'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { CsvButton } from '../list/CsvButton'
import { CustomerTable } from './CustomerTable'
import { useCustomersList } from './customersList'
import { useCustomerSearch } from './customerSearch'
import { CustomerFilters, NoRows } from './Filters'
import { useCustomerParams, useCustomersQuery } from './params'
import { useRevealFlow } from './Reveal'
import { SearchBox, SearchResults } from './Search'
import { SignupsPanel } from './SignupsPanel'
import '../list/list.css'
import './customers.css'

/**
 * Analytics › Customers: the period's sign-ups, and every customer in scope, one row
 * each, with what they bought over their lifetime and in the period
 * (`GET /analytics/customers`). Customers are masked as served. The API sorts and pages
 * across every customer, so a header click asks it for a new order and "Show more"
 * fetches the next page. One box finds a person by email (or, for a role that may
 * reveal ids, by website customer id), and such a role can reveal one customer's full
 * id, audited, after saying so. There is no customer page to open yet, so rows are not
 * links.
 */
export function CustomersPage({ route }: { route: RouteDef }) {
  return (
    <SectionFrame route={route}>
      <div className="cus-page">
        <SignupsPanel />
        <CustomersCard />
      </div>
    </SectionFrame>
  )
}

/** The list's request, its card, the search and the reveal, and the loading and outage states. */
function CustomersCard() {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  const params = useCustomerParams(filters)
  const { scope, query } = useCustomersQuery(filters, params)
  const list = useCustomersList(query)
  const search = useCustomerSearch(scope, { sort: params.sort, order: params.order })
  // A new search box after "Clear search", so what was typed goes with the search.
  const [searchBox, setSearchBox] = useState(0)
  const { can } = usePermissions()
  const canReveal = can('customers.reveal')
  const { folded } = list
  usePageFreshness(folded?.meta)
  // While the previous list stands in for the next, its period is not the one a download would cover.
  const period = list.isPlaceholderData ? undefined : folded?.meta.period

  const reveal = useRevealFlow(canReveal)

  const csv = can('exports.create') ? (
    <CsvButton
      key={JSON.stringify(query)}
      download={() => client.customersCsv(query)}
      filters={query.filters}
      period={period}
      filename={customersCsvFilename}
      noun="customers"
      maxRows={CUSTOMERS_CSV_MAX_ROWS}
      hint="Masked: customer refs only, never full ids"
    />
  ) : null

  return (
    <Card
      title="Customer list"
      subtitle={folded?.meta.period ? `${folded.meta.period.label} · lifetime and period figures per customer, masked` : undefined}
      busy={list.isFetching && !list.isFetchingNextPage}
      actions={csv}
    >
      <div className="list-body">
        <div className="list-toolbar">
          <SearchBox
            key={searchBox}
            canSearchById={canReveal}
            searching={search.state.status === 'loading'}
            onSearch={search.run}
          />
        </div>
        <CustomerFilters params={params} />
        {reveal.confirm}
        {search.state.status !== 'idle' ? (
          <SearchResults
            state={search.state}
            params={params}
            identity={reveal.identity}
            onClear={() => {
              search.clear()
              setSearchBox((box) => box + 1)
            }}
          />
        ) : list.isPending ? (
          <LoadingBlock rows={6} label="Loading customers" />
        ) : !folded ? (
          <EmptyState variant="outage" announce description="The customers list could not be loaded just now. Try again in a moment." />
        ) : (
          <CustomerTable
            result={folded}
            params={params}
            paging={list.paging}
            empty={<NoRows params={params} scoped={filters.dimensions.website !== undefined || filters.environments.length > 0} />}
            identity={reveal.identity}
          />
        )}
      </div>
    </Card>
  )
}
